import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import IconChevronLeft from "@/icons/icon-chevron-left";
import Grid from "~/components/common/grid";
import myaccount from "~/components/common/myaccount.module.scss";
import Navigation from "~/components/myaccount/navigation";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { basicAnimationStates } from "~/utils/animation-utils";
import appRoutes from "~/utils/app-routes";

import Container from "../common/container";
import Icon from "../common/icon";
import CustomerCare from "../customer-care/customer-care";

type Props = {
  children: JSX.Element;
};
export default function MyAccountPage({ children }: Props) {
  const { user } = useAuthContext();
  const breakpoint = useBreakpoint();
  const router = useRouter();
  const t = useTranslations();

  // this should correspond to myAccountRoutes mapping in navigation.tsx
  const currentPath = router.asPath.split("/")[2];
  const onChildPage = !!router.asPath.split("/")[3];
  const completeSlug = Array.isArray(router.query.slug) ? router.query.slug.join("-") : router.query.slug;

  return (
    <Container className={myaccount.myaccountPageWrapper}>
      <Grid>
        {breakpoint == "desktop" || !currentPath ? (
          <span className={myaccount.greetUser}>
            {t.rich("generic.hello_user_myaccount", {
              span: (chunks) => <span>{chunks}</span>,
              name: user?.FirstName,
            })}
          </span>
        ) : onChildPage ? null : (
          <div className={myaccount.mobileHeading}>
            <Link className={myaccount.backMobile} href={appRoutes.MYACCOUNT}>
              <Icon icon={IconChevronLeft} aria-hidden />
            </Link>
            <h1 className={myaccount.mobileHeadingTitle}>
              {t(`generic.myaccount.nav.${currentPath == "" ? "dashboard" : currentPath.split("-").join("")}`)}
            </h1>
          </div>
        )}
        {breakpoint == "desktop" && (
          <div className={myaccount.sidebar}>
            <Navigation />
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={`myaccout-${completeSlug}`}
            initial={basicAnimationStates.off}
            animate={basicAnimationStates.on}
            exit={basicAnimationStates.off}
            transition={{ duration: 0.3, opacity: { delay: 0.05 } }}
            className={myaccount.content}
          >
            {!(currentPath == "returns" || currentPath == "purchases" || currentPath == "preferred-store") &&
              breakpoint == "desktop" && (
                <h1 className="sr-only">
                  {t(`generic.myaccount.nav.${currentPath == "" ? "dashboard" : currentPath.split("-").join("")}`)}
                </h1>
              )}
            {currentPath == "" && breakpoint == "mobile" && (
              <h1 className="sr-only">{t("generic.myaccount.nav.dashboard")}</h1>
            )}
            {children}
            {/* show navigation on mobile only on dashboard */}
            {breakpoint == "mobile" && !currentPath && <Navigation />}
            <CustomerCare />
          </motion.div>
        </AnimatePresence>
      </Grid>
    </Container>
  );
}
